import { apiUrl, ensureAccessToken, fetchRemoteLots, type ApiConfig } from './api';
import { showToast } from './ui';
import { State } from './types';

let urlImportTimeout: ReturnType<typeof setTimeout> | null = null;
let pendingImportUrl: string | null = null;

export type ImportSource = 'hibid' | 'kbid' | 'mnbid' | 'generic';

/**
 * Classify a pasted URL: known sites → their bespoke scraper, any other
 * public https page → AI-assisted generic import. Null = not importable.
 */
export function detectImportableSource(url: string): ImportSource | null {
  let parsed: URL;
  try {
    // If URL doesn't have a protocol (e.g., "mnbid.mn.gov/productView/14192"),
    // assume https:// and retry.
    let urlToTest = url;
    if (!url.match(/^[a-z][a-z0-9+.-]*:/i)) {
      urlToTest = 'https://' + url;
    }
    parsed = new URL(urlToTest);
  } catch {
    return null;
  }
  const protocol = parsed.protocol.toLowerCase();
  if (protocol !== 'https:' && protocol !== 'http:') return null;

  const hostname = parsed.hostname.toLowerCase();
  const matches = (domain: string) => hostname === domain || hostname.endsWith('.' + domain);
  if (matches('hibid.com')) return 'hibid';
  if (matches('k-bid.com')) return 'kbid';
  if (matches('mnbid.mn.gov')) return 'mnbid';

  // Generic import mirrors the server's guard: https + a real public host.
  if (protocol !== 'https:') return null;
  if (!hostname.includes('.') || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return null;
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return null;
  return 'generic';
}

/** Human label for the confirm dialog. */
function describeImportSource(url: string, source: ImportSource): string {
  if (source === 'hibid') return 'HiBid.com';
  if (source === 'kbid') return 'K-BID.com';
  if (source === 'mnbid') return 'Minnesota Surplus (MNBID)';
  try {
    return new URL(url).hostname + ' (AI-assisted)';
  } catch {
    return 'this site (AI-assisted)';
  }
}

export function showImportConfirmation(
  url: string,
  source: ImportSource,
  importConfirmBackdrop: HTMLElement,
  importConfirmModal: HTMLElement,
  importConfirmSource: HTMLElement
): void {
  pendingImportUrl = url;
  if (importConfirmSource) {
    importConfirmSource.textContent = describeImportSource(url, source);
  }
  if (importConfirmBackdrop) importConfirmBackdrop.removeAttribute('hidden');
  if (importConfirmModal) importConfirmModal.removeAttribute('hidden');
}

export function hideImportConfirmation(
  importConfirmBackdrop: HTMLElement,
  importConfirmModal: HTMLElement
): void {
  pendingImportUrl = null;
  if (importConfirmBackdrop) importConfirmBackdrop.setAttribute('hidden', '');
  if (importConfirmModal) importConfirmModal.setAttribute('hidden', '');
}

export function getPendingImportUrl(): string | null {
  return pendingImportUrl;
}

export async function performImport(
  url: string,
  cfg: ApiConfig,
  closeLotModal: (options: { reset: boolean }) => void,
  render: () => void
): Promise<void> {
  try {
    closeLotModal({ reset: true });

    // Normalize URL: prepend https:// if no protocol present
    let normalizedUrl = url;
    if (!url.match(/^[a-z][a-z0-9+.-]*:/i)) {
      normalizedUrl = 'https://' + url;
    }

    const isGeneric = detectImportableSource(normalizedUrl) === 'generic';
    showToast(
      isGeneric ? 'Importing with AI — this can take ~20 seconds...' : 'Importing lot data...',
      { type: 'info' }
    );

    const token = await ensureAccessToken();

    const requestBody = { url: normalizedUrl };

    const response = await fetch(apiUrl('extensions/scrapes/fetch'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMsg = errorData.error || `HTTP ${response.status}`;
      let details = '';
      if (errorData.details) {
        if (Array.isArray(errorData.details)) {
          details = `: ${errorData.details.map((d: { message?: string; [key: string]: unknown }) => d.message || JSON.stringify(d)).join(', ')}`;
        } else {
          details = `: ${errorData.details}`;
        }
      }
      throw new Error(errorMsg + details);
    }

    const result = await response.json();

    showToast('Lot imported successfully!', { type: 'success' });
    // Use simpler fetchRemoteLots overload from api.ts (cfg only)
    await fetchRemoteLots(cfg || null);
    render();
  } catch (error) {
    showToast(`Import failed: ${(error as Error).message}`, { type: 'error' });
  }
}

export function setupUrlImportListeners(
  els: { f?: { url?: HTMLInputElement } | null } & Record<string, unknown>,
  state: State,
  cfg: ApiConfig,
  closeLotModal: (options: { reset: boolean }) => void,
  render: () => void,
  importConfirmBackdrop: HTMLElement | null,
  importConfirmModal: HTMLElement | null,
  importConfirmSource: HTMLElement | null,
  importConfirmYes: HTMLElement | null,
  importConfirmNo: HTMLElement | null
): void {
  if (!importConfirmBackdrop || !importConfirmModal || !importConfirmYes || !importConfirmNo) {
    return;
  }

  if (importConfirmYes) {
    importConfirmYes.addEventListener('click', () => {
      const urlToImport = pendingImportUrl;
      hideImportConfirmation(importConfirmBackdrop, importConfirmModal);
      if (urlToImport) {
        performImport(urlToImport, cfg, closeLotModal, render);
      }
    });
  }

  if (importConfirmNo) {
    importConfirmNo.addEventListener('click', () => {
      hideImportConfirmation(importConfirmBackdrop, importConfirmModal);
    });
  }

  if (els.f?.url) {
    const scheduleConfirmation = (delay: number) => {
      const url = els.f?.url?.value.trim();
      if (!url) return;

      const source = detectImportableSource(url);
      if (!source) return;

      if (state.mode !== 'auth0') return;

      urlImportTimeout = setTimeout(() => {
        if (importConfirmBackdrop && importConfirmModal && importConfirmSource) {
          showImportConfirmation(
            url,
            source,
            importConfirmBackdrop as HTMLElement,
            importConfirmModal as HTMLElement,
            importConfirmSource as HTMLElement
          );
        }
      }, delay);
    };

    els.f.url.addEventListener('input', () => {
      if (urlImportTimeout) clearTimeout(urlImportTimeout as any);
      scheduleConfirmation(800);
    });

    els.f.url.addEventListener('paste', () => {
      if (urlImportTimeout) clearTimeout(urlImportTimeout as any);
      setTimeout(() => scheduleConfirmation(300), 10);
    });
  }
}
